import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import NProgress from 'nprogress'

// 路由配置
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '超管登录', hideLayout: true }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layout/index.vue'),
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '数据总览', icon: 'DataAnalysis' }
      },
      {
        path: 'tenants',
        name: 'Tenants',
        component: () => import('@/views/Tenants.vue'),
        meta: { title: '租户管理', icon: 'User' }
      },
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('@/views/Orders.vue'),
        meta: { title: '购买工具记录', icon: 'List' }
      },
      {
        path: 'recharge-records',
        name: 'RechargeRecords',
        component: () => import('@/views/RechargeRecords.vue'),
        meta: { title: '星币充值', icon: 'CreditCard' }
      },
      {
        path: 'task-management',
        name: 'TaskManagement',
        component: () => import('@/views/TaskManagement.vue'),
        meta: { title: '历史记录管理', icon: 'List' }
      },
      {
        path: 'cloud-spend-logs',
        name: 'CloudSpendLogs',
        component: () => import('@/views/CloudSpendLogs.vue'),
        meta: { title: '云服务记录', icon: 'MagicStick', requiresAuth: true }
      },

      {
        path: 'system-settings',
        name: 'SystemSettings',
        component: () => import('@/views/SystemSettings.vue'),
        meta: { title: '系统设置', icon: 'Tools' }
      },
      {
        path: 'upgrade-release',
        name: 'UpgradeReleaseCenter',
        component: () => import('@/views/UpgradeReleaseCenter.vue'),
        meta: { title: '版本发布中心', icon: 'UploadFilled', requiresAuth: true }
      },
      {
        path: 'store',
        name: 'StoreManagement',
        meta: { title: '商城管理', icon: 'Shop' },
        children: [
          {
            path: 'tools',
            name: 'ToolsList',
            component: () => import('@/views/ToolsList.vue'),
            meta: { title: '工具列表', icon: 'Tools' }
          },
          {
            path: 'categories',
            name: 'ToolsCategories',
            component: () => import('@/views/store/ToolsCategories.vue'),
            meta: { title: '工具分类', icon: 'Menu' }
          },

          {
            path: 'codes',
            name: 'ShopCodes',
            component: () => import('@/views/ShopCodes.vue'),
            meta: { title: '兑换码管理', icon: 'Ticket' }
          }
        ]
      },
      {
        path: 'miniprogram',
        name: 'MiniProgramManager',
        component: () => import('../views/MiniProgramManager.vue'),
        meta: { 
          title: '小程序管理',
          requiresAuth: true 
        }
      },
      {
        path: 'domain-management',
        name: 'DomainManagement',
        component: () => import('@/views/DomainManagement.vue'),
        meta: { 
          title: '域名管理',
          icon: 'Monitor',
          requiresAuth: true 
        }
      },
      // API 商城：全部展平为 Layout 下一级子路由（单 router-view），避免嵌套 ApiMarketLayout 刷新白屏
      {
        path: 'api-market',
        redirect: '/api-market/plugins'
      },
      {
        path: 'api-market/categories',
        name: 'ApiCategories',
        component: () => import('@/views/ApiCategories.vue'),
        meta: { title: '分类管理', icon: 'FolderOpened', requiresAuth: true }
      },
      {
        path: 'api-market/skills',
        name: 'ApiSkills',
        component: () => import('@/views/ApiSkills.vue'),
        meta: { title: '技能管理', icon: 'Guide', requiresAuth: true }
      },
      {
        path: 'api-market/plugins',
        name: 'ApiPlugins',
        component: () => import('@/views/ApiPlugins.vue'),
        meta: { title: 'API接口', icon: 'Connection', requiresAuth: true }
      },
      {
        path: 'api-market/key-pools',
        name: 'KeyPools',
        component: () => import('@/views/KeyPools.vue'),
        meta: { title: 'Key池管理', icon: 'Key', requiresAuth: true }
      },
      {
        path: 'api-market/recharge-packages',
        name: 'RechargePackages',
        component: () => import('@/views/RechargePackages.vue'),
        meta: { title: '充值套餐', icon: 'Wallet', requiresAuth: true }
      },
      {
        path: 'api-market/membership-plans',
        name: 'MembershipPlans',
        component: () => import('@/views/MembershipPlans.vue'),
        meta: { title: '会员套餐', icon: 'Medal', requiresAuth: true }
      },
      {
        path: 'api-market/logs',
        name: 'ApiLogs',
        component: () => import('@/views/ApiLogs.vue'),
        meta: { title: '使用日志', icon: 'Document', requiresAuth: true }
      },
      {
        path: 'api-market/statistics',
        name: 'ApiStatistics',
        component: () => import('@/views/ApiStatistics.vue'),
        meta: { title: 'API统计', icon: 'DataAnalysis', requiresAuth: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    meta: { title: '页面不存在', hideLayout: true }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  NProgress.start()
  
  const authStore = useAuthStore()
  
  // 嵌套路由：子路由单独没有 requiresAuth 时，向上找父级（与 Layout 一致）
  const needsAuth = to.matched.some((record) => record.meta.requiresAuth)

  // 如果需要认证的页面
  if (needsAuth) {
    // 如果当前没有认证状态，先尝试初始化认证
    if (!authStore.isAuthenticated) {
      const token = localStorage.getItem('super_admin_token')
      if (token) {
        console.log('🔄 路由守卫：尝试恢复认证状态...')
        // 等待认证检查完成
        const isValid = await authStore.checkAuth()
        if (isValid) {
          console.log('✅ 路由守卫：认证恢复成功')
          next()
        } else {
          console.log('❌ 路由守卫：认证失败，跳转登录')
          next('/login')
        }
      } else {
        console.log('🔐 路由守卫：无Token，跳转登录')
        next('/login')
      }
    } else {
      // 已经认证，允许访问
      next()
    }
  } else {
    // 不需要认证的页面，直接放行
    next()
  }
})

router.afterEach(() => {
  NProgress.done()
})

export default router
